/**
 * NoteDoc — one Y.Doc per note, with its own y-partyserver provider and
 * IndexedDB persistence. Each NoteDoc routes to the server's
 * `?docKey=note\x01{deckId}\x01{noteId}` endpoint, which the server's
 * `MultiDocYServer` resolves to a single per-note row in
 * `deck_docs`/`sg_docs`.
 *
 * INVARIANTS
 * ----------
 * - One WebSocket per active note (in addition to the root WS the
 *   `UserDoConnectionProvider` / study-group sync hook owns).
 * - One IndexedDB store per note (named `flashka-note:{room}:{deckId}:{noteId}`
 *   with `\x01` collapsed to `:` for human-readable inspection — the
 *   protocol's docKey itself uses `\x01`).
 * - **`disableBc: true` is mandatory.** y-partyserver's BroadcastChannel
 *   key is `${serverUrl}/${roomname}` and ignores query params; without
 *   `disableBc`, two NoteDoc providers in different tabs would
 *   cross-pollinate updates intended for different notes. See
 *   node_modules/y-partyserver/dist/provider/index.js:243.
 *
 * SCHEMA (the per-note Y.Doc itself)
 * ----------------------------------
 *   Y.Doc
 *     └─ root keys: { type: NoteKind, content: Y.XmlFragment | scalars }
 *
 * Per kind:
 *   tiptap  → { type, content: Y.XmlFragment, title? }
 *   youtube → { type, url, title?, thumbnail?, description? }
 *   link    → { type, url, title?, description?, favicon? }
 *
 * The shape is `Y.XmlFragment` for tiptap plus scalar `Y.Map` keys for
 * the rest. We intentionally store the kind on the doc's root map so a
 * non-loaded note can be classified without touching the fragment.
 */

import { notifySocketCredentialRejected } from '@flashka/shared/auth/jwt-cache';
import { noteDocKey } from '@flashka/shared/sync-engine/framework/doc-keys';

import YProvider from 'y-partyserver/provider';
import * as Y from 'yjs';

import {
  NOTE_META_MAP_KEY,
  TIPTAP_NOTE_FRAGMENT_KEY,
} from '../../notes/tiptap-note-kind';
import { pushSyncMessage, type SyncMessageSource } from '../internal/message-log';
import { createYjsPersistence, type YjsPersistence } from '../internal/yjs-persistence';
import {
  type LinkNoteMeta,
  NOTE_ITEM_KEYS,
  type NoteKind,
  type YoutubeNoteMeta,
} from './deck-doc';

/**
 * Map a NoteDoc party (`user-do` / `study-group-do`) to the message
 * log's `source` enum so devtools can colour-code per-DO traffic.
 */
function logSourceForParty(party: string): SyncMessageSource {
  return party === 'study-group-do' ? 'study-group-do' : 'user-do';
}

/**
 * Categorised error surface so consumers (the editor, devtools) can
 * react to specific failures. `kind: "ws-error"` is a transport-level
 * connection error; `"ws-closed"` covers an unclean server-initiated
 * close; `"yjs-error"` is a Yjs internal error event (e.g. the doc
 * receiving a malformed update); `"idb-error"` is a local
 * IndexedDB persistence failure.
 */
export type NoteDocError = {
  kind: 'ws-error' | 'ws-closed' | 'yjs-error' | 'idb-error';
  message: string;
  ts: number;
};

// ---------------------------------------------------------------------------
// Y.Doc layout
// ---------------------------------------------------------------------------

/**
 * Root Y.Map on a NoteDoc — holds the kind + scalar metadata. The
 * tiptap fragment lives at a separate root `content` Y.XmlFragment.
 */
const META_MAP_KEY = NOTE_META_MAP_KEY;
const TIPTAP_FRAGMENT_KEY = TIPTAP_NOTE_FRAGMENT_KEY;
/**
 * Root Y.Map for the "Read & Repeat" study mode — keyed by paragraph
 * `data-id` (stamped by the Tiptap UniqueID extension), value `true`
 * when the paragraph has been completed. Lives on the same per-note
 * Y.Doc so it inherits IDB persistence + WS sync for free; a missing
 * key means "not completed yet".
 */
const READ_REPEAT_MAP_KEY = 'readAndRepeat';

/**
 * Sibling Y.Map storing the most recent recall attempt per paragraph.
 * Same key as the completion map (paragraph `data-id`); value is a
 * snapshot of the user's transcript + grading result. Last-write-wins
 * per key — concurrent attempts from two devices on the same
 * paragraph would race, but in practice a single user records on one
 * device at a time so this is fine for the MVP.
 */
const READ_REPEAT_ATTEMPTS_KEY = 'readAndRepeatAttempts';

/**
 * Persisted snapshot of one recall attempt.
 *
 * Two grading layers coexist on the record:
 *   - `covered` / `missed`: legacy word-level lists. Always present —
 *     populated by the mock fallback or derived from `spans` when the
 *     AI call succeeded. Useful for simple consumers (history view,
 *     analytics).
 *   - `spans`: passage-aligned excerpts classified `correct` /
 *     `inaccurate` / `missing`. Drives the inline highlight overlay
 *     when the recall is replayed. Optional — absent on mock fallback
 *     attempts that didn't try the AI.
 */
export type RecallSpanStatus = 'correct' | 'inaccurate' | 'missing';

export interface RecallSpan {
  /** Verbatim excerpt of the original passage this span covers. */
  passageText: string;
  status: RecallSpanStatus;
  /** Constructive note explaining the imprecision. Set only for
   *  `inaccurate`; null otherwise. */
  note: string | null;
}

/**
 * Structured feedback rendered as remembered / missed cards.
 * Optional on the attempt — only present when the AI grader
 * succeeded; the mock fallback path leaves it undefined.
 */
export interface RecallFeedback {
  remembered: string[];
  missed: string[];
}

export interface RecallAttempt {
  transcript: string;
  covered: string[];
  missed: string[];
  /** Passage-span grading from the LLM evaluator (or the mock
   *  fallback). Drives the inline color overlay when an attempt is
   *  replayed. Optional. */
  spans?: RecallSpan[];
  /** Three-card feedback (remembered / missed / takeaway). Optional;
   *  absent when the AI fell back to the mock grader. */
  feedback?: RecallFeedback;
  atISO: string;
}

// ---------------------------------------------------------------------------
// NoteDoc
// ---------------------------------------------------------------------------

export interface NoteDocLocation {
  /** Server room — userId for personal notes, groupId for group notes. */
  room: string;
  /** Which DO party this note belongs to (e.g. "user-do" / "study-group-do"). */
  party: string;
  deckId: string;
  noteId: string;
}

export interface NoteDocOptions extends NoteDocLocation {
  host: string;
  /** y-partyserver provider params (typically a `getToken` async fn). */
  params?: Record<string, string> | (() => Promise<Record<string, string>>);
}

export class NoteDoc {
  readonly doc: Y.Doc;
  readonly provider: YProvider;
  readonly idb: YjsPersistence;
  readonly location: NoteDocLocation;

  private _idbSynced = false;
  private _wsSynced = false;
  private _lastError: NoteDocError | null = null;
  private listeners = new Set<() => void>();
  private _disposed = false;

  constructor(opts: NoteDocOptions) {
    this.location = {
      room: opts.room,
      party: opts.party,
      deckId: opts.deckId,
      noteId: opts.noteId,
    };

    this.doc = new Y.Doc();
    const docKey = noteDocKey(opts.deckId, opts.noteId);

    // Build the params resolver. We always inject docKey alongside any
    // user-supplied params (e.g. auth token). The provider also adds
    // `_pk={connectionId}` — neither layer cares about ordering.
    const docKeyParam = { docKey };
    const userParams = opts.params;
    const params: NoteDocOptions['params'] =
      typeof userParams === 'function'
        ? async () => ({ ...(await userParams()), ...docKeyParam })
        : { ...(userParams ?? {}), ...docKeyParam };

    this.provider = new YProvider(opts.host, opts.room, this.doc, {
      party: opts.party,
      params,
      // CRITICAL: see file header. Two providers on the same room with
      // different docKey share a BroadcastChannel and corrupt each other.
      disableBc: true,
    });

    // One persistence handle per note. On web this resolves to an IDB
    // store. The store name is a clean text encoding of the docKey so
    // it's debuggable in DevTools.
    this.idb = createYjsPersistence(
      `flashka-note:${opts.room}:${opts.deckId}:${opts.noteId}`,
      this.doc,
    );

    this.idb.on('synced', this.handleIdbSynced);
    this.provider.on('sync', this.handleWsSynced);
    if (this.provider.synced) {
      this._wsSynced = true;
    }

    // ── Devtools message-log wiring ──────────────────────────────────
    // Every Yjs transaction emits an `update` event with a binary
    // payload + an `origin`. Classify by origin so the unified message
    // log distinguishes local edits from remote applies and IDB
    // replays — same convention `hooks.tsx` uses for the root doc.
    this.doc.on('update', this.handleDocUpdate);

    // ── Error wiring ─────────────────────────────────────────────────
    // y-partyserver's provider emits `connection-error` for transport
    // failures (DNS, TLS, 4xx/5xx upgrade rejections) and
    // `connection-close` after the server hangs up the socket.
    //
    // Without this wiring a stuck NoteDoc surfaces as an empty editor
    // div (notes-view returns null when the fragment hasn't appeared),
    // so the user has no signal that anything went wrong. Publish each
    // category to the devtools log + console + an in-memory `lastError`
    // so consumers can render a fallback UI.
    this.provider.on('connection-error', this.handleProviderError);
    this.provider.on('connection-close', this.handleProviderClose);
  }

  // ── Sync state ────────────────────────────────────────────────────

  /** True once IDB has replayed cached state into the Y.Doc. */
  get idbSynced(): boolean {
    return this._idbSynced;
  }

  /** True once the WS Yjs sync handshake (sync-step2) has completed. */
  get wsSynced(): boolean {
    return this._wsSynced;
  }

  /**
   * True when it's safe to auto-create empty Y types (e.g. an empty
   * Y.XmlFragment for a brand-new note). Same gate as the previous
   * single-Y.Doc world — never auto-create on a pre-sync doc, that
   * races the server's sync-step2 reply and tombstones content.
   */
  get synced(): boolean {
    return this._wsSynced;
  }

  /** The most recent connection / sync error, if any. Cleared on the
   *  next successful sync handshake. Consumers can use this to render
   *  a "couldn't connect" UI for a stuck note. */
  get lastError(): NoteDocError | null {
    return this._lastError;
  }

  /** Subscribe to sync-state / error changes. Returns an unsubscribe fn. */
  onChange(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  // ── Note kind + metadata ──────────────────────────────────────────

  private metaMap(): Y.Map<unknown> {
    return this.doc.getMap<unknown>(META_MAP_KEY);
  }

  /** The note's kind as recorded on the doc's `meta` map. */
  getKind(): NoteKind | undefined {
    const t = this.metaMap().get(NOTE_ITEM_KEYS.type);
    if (t === 'tiptap' || t === 'youtube' || t === 'link') return t;
    return undefined;
  }

  /**
   * Get the tiptap Y.XmlFragment, creating one if the doc is empty AND
   * it's safe to do so (caller has already gated on `synced`).
   *
   * Returns `null` if the doc has a different kind set on its meta map.
   * Returns the existing fragment if one is already present.
   * Otherwise creates a new fragment + stamps `type: "tiptap"`.
   *
   * **Auto-create gate**: callers MUST check `synced` before calling
   * this with the intent to create a new note. Calling on an unsynced
   * doc creates an empty fragment locally that races the server's
   * sync-step2 reply through Yjs's tiebreaker. See `notes-view.tsx`'s
   * `notesSynced` gate for the canonical pattern.
   */
  getOrCreateTiptapFragment(): Y.XmlFragment | null {
    const kind = this.getKind();
    if (kind !== undefined && kind !== 'tiptap') return null;
    const frag = this.doc.getXmlFragment(TIPTAP_FRAGMENT_KEY);
    if (kind === undefined) {
      this.metaMap().set(NOTE_ITEM_KEYS.type, 'tiptap');
    }
    return frag;
  }

  /**
   * Side-effect-free read of the tiptap fragment. Returns `null` if the
   * note has a different kind, or if the fragment hasn't been created
   * yet (no content has been written).
   *
   * A note with NO recorded kind but a non-empty `content` fragment is a
   * tiptap note too: mobile's DOM editor writes the fragment directly and
   * older app builds never stamped `meta.type`. Reading it as "no content"
   * made every web emptiness check treat a phone-edited note as empty.
   */
  peekTiptapFragment(): Y.XmlFragment | null {
    const kind = this.getKind();
    if (kind !== undefined && kind !== 'tiptap') return null;
    const frag = this.doc.getXmlFragment(TIPTAP_FRAGMENT_KEY);
    if (kind === undefined && frag.length === 0) return null;
    return frag;
  }

  /** YouTube metadata. `undefined` if the note has a different kind. */
  getYoutubeMeta(): YoutubeNoteMeta | undefined {
    if (this.getKind() !== 'youtube') return undefined;
    const m = this.metaMap();
    const url = m.get(NOTE_ITEM_KEYS.url) as string | undefined;
    if (!url) return undefined;
    return {
      url,
      title: m.get(NOTE_ITEM_KEYS.title) as string | undefined,
      thumbnail: m.get(NOTE_ITEM_KEYS.thumbnail) as string | undefined,
      description: m.get(NOTE_ITEM_KEYS.description) as string | undefined,
    };
  }

  setYoutubeMeta(meta: YoutubeNoteMeta): void {
    this.doc.transact(() => {
      const m = this.metaMap();
      m.set(NOTE_ITEM_KEYS.type, 'youtube');
      m.set(NOTE_ITEM_KEYS.url, meta.url);
      if (meta.title != null) m.set(NOTE_ITEM_KEYS.title, meta.title);
      if (meta.thumbnail != null) m.set(NOTE_ITEM_KEYS.thumbnail, meta.thumbnail);
      if (meta.description != null) m.set(NOTE_ITEM_KEYS.description, meta.description);
    });
  }

  /** Link metadata. `undefined` if the note has a different kind. */
  getLinkMeta(): LinkNoteMeta | undefined {
    if (this.getKind() !== 'link') return undefined;
    const m = this.metaMap();
    const url = m.get(NOTE_ITEM_KEYS.url) as string | undefined;
    if (!url) return undefined;
    return {
      url,
      title: m.get(NOTE_ITEM_KEYS.title) as string | undefined,
      description: m.get(NOTE_ITEM_KEYS.description) as string | undefined,
      favicon: m.get(NOTE_ITEM_KEYS.favicon) as string | undefined,
    };
  }

  setLinkMeta(meta: LinkNoteMeta): void {
    this.doc.transact(() => {
      const m = this.metaMap();
      m.set(NOTE_ITEM_KEYS.type, 'link');
      m.set(NOTE_ITEM_KEYS.url, meta.url);
      if (meta.title != null) m.set(NOTE_ITEM_KEYS.title, meta.title);
      if (meta.description != null) m.set(NOTE_ITEM_KEYS.description, meta.description);
      if (meta.favicon != null) m.set(NOTE_ITEM_KEYS.favicon, meta.favicon);
    });
  }

  // ── Read & Repeat progress ────────────────────────────────────────
  //
  // Per-paragraph completion state for the in-notes "Read & Repeat"
  // study mode. Key = the paragraph's `data-id` (stamped by Tiptap's
  // UniqueID extension); value = `true` when completed. We never
  // store `false` — absence is the "not completed" state. Caller
  // must gate writes on `synced` (same rule as
  // `getOrCreateTiptapFragment`).

  /** Get the Y.Map of completed paragraph ids. Auto-creates an empty
   *  map on first access — safe even pre-sync because Y.Map creation
   *  alone doesn't write content. */
  readAndRepeatMap(): Y.Map<boolean> {
    return this.doc.getMap<boolean>(READ_REPEAT_MAP_KEY);
  }

  /** Mark a paragraph completed (or clear). Writes are no-ops until
   *  the doc is `synced` to avoid the same race
   *  `getOrCreateTiptapFragment` warns about. */
  setParagraphCompleted(paragraphId: string, completed: boolean): void {
    if (!this.synced) return;
    this.doc.transact(() => {
      const map = this.readAndRepeatMap();
      if (completed) {
        map.set(paragraphId, true);
      } else {
        map.delete(paragraphId);
      }
    });
  }

  /** Reset every paragraph's completion AND clear all stored recall
   *  attempts. Used by the "Reset" button in the Read & Repeat
   *  sidebar — keeping the two maps in lockstep avoids stranded
   *  attempt records pointing at paragraphs the user is replaying
   *  from scratch. */
  clearReadAndRepeat(): void {
    if (!this.synced) return;
    this.doc.transact(() => {
      this.readAndRepeatMap().clear();
      this.readAndRepeatAttemptsMap().clear();
    });
  }

  /** Y.Map of {paragraphId → most-recent RecallAttempt}. Auto-created
   *  on first access (Y.Map creation alone is safe pre-sync). */
  readAndRepeatAttemptsMap(): Y.Map<RecallAttempt> {
    return this.doc.getMap<RecallAttempt>(READ_REPEAT_ATTEMPTS_KEY);
  }

  /** Stamp the most recent recall attempt for a paragraph. No-op
   *  until the doc is synced (same gate rationale as the completion
   *  setter). */
  setRecallAttempt(paragraphId: string, attempt: RecallAttempt): void {
    if (!this.synced) return;
    this.doc.transact(() => {
      this.readAndRepeatAttemptsMap().set(paragraphId, attempt);
    });
  }

  /** True when the note has any user-meaningful content. */
  hasContent(): boolean {
    const kind = this.getKind();
    if (kind === 'tiptap' || kind === undefined) {
      const frag = this.peekTiptapFragment();
      return frag != null && frag.length > 0;
    }
    if (kind === 'youtube' || kind === 'link') {
      return Boolean(this.metaMap().get(NOTE_ITEM_KEYS.url));
    }
    return false;
  }

  // ── Lifecycle ─────────────────────────────────────────────────────

  /** True once `dispose()` (or `destroyAndEraseLocal()`) has been called. */
  get disposed(): boolean {
    return this._disposed;
  }

  /**
   * Tear down the provider + IDB connection. Keeps the local IDB store
   * intact so the next `NoteDoc` for the same note rehydrates instantly.
   * Use {@link destroyAndEraseLocal} when removing the note for good.
   */
  async dispose(): Promise<void> {
    if (this._disposed) return;
    this._disposed = true;
    this.listeners.clear();
    this.idb.off('synced', this.handleIdbSynced);
    this.provider.off('sync', this.handleWsSynced);
    this.provider.off('connection-error', this.handleProviderError);
    this.provider.off('connection-close', this.handleProviderClose);
    this.doc.off('update', this.handleDocUpdate);
    try {
      this.provider.destroy();
    } catch {
      // ignore — provider may already be torn down
    }
    try {
      // `destroy()` here closes the IDB connection but leaves the
      // stored data on disk for next time.
      await this.idb.destroy();
    } catch {
      // ignore
    }
    this.doc.destroy();
  }

  /**
   * Tear down AND wipe the persisted IDB data. Use when the note is
   * being deleted. `clearData()` removes the IndexedDB store entirely;
   * `destroy()` only closes the connection.
   */
  async destroyAndEraseLocal(): Promise<void> {
    if (this._disposed) {
      // Recreate a transient persistence handle just to wipe the data.
      try {
        const idb = createYjsPersistence(
          `flashka-note:${this.location.room}:${this.location.deckId}:${this.location.noteId}`,
          new Y.Doc(),
        );
        await idb.clearData();
        await idb.destroy();
      } catch {
        // ignore
      }
      return;
    }
    this._disposed = true;
    this.listeners.clear();
    this.idb.off('synced', this.handleIdbSynced);
    this.provider.off('sync', this.handleWsSynced);
    this.provider.off('connection-error', this.handleProviderError);
    this.provider.off('connection-close', this.handleProviderClose);
    this.doc.off('update', this.handleDocUpdate);
    try {
      this.provider.destroy();
    } catch {
      // ignore
    }
    try {
      await this.idb.clearData();
    } catch {
      // ignore
    }
    this.doc.destroy();
  }

  // ── Private handlers (arrow methods so `off` matches the same ref) ─

  private handleIdbSynced = (): void => {
    this._idbSynced = true;
    this.notify();
  };

  private handleWsSynced = (isSynced: boolean): void => {
    if (isSynced) {
      this._wsSynced = true;
      // Clear any previous error — we're connected and synced.
      if (this._lastError) this._lastError = null;
      this.notify();
    }
  };

  private setError(err: NoteDocError): void {
    this._lastError = err;
    const tag = `[NoteDoc:${this.location.party}/${this.location.deckId}/${this.location.noteId}]`;

    console.error(`${tag} ${err.kind}: ${err.message}`);
    pushSyncMessage({
      source: logSourceForParty(this.location.party),
      direction: 'in',
      type: `yjs-${err.kind}`,
      ts: err.ts,
      sizeBytes: 0,
      // Include room (groupId/userId) so the devtools SG messages
      // tab filter (`detail.includes(selectedGroupId)`) keeps these.
      detail: `room ${this.location.room} note ${this.location.deckId}/${this.location.noteId}: ${err.message.slice(
        0,
        160,
      )}`,
    });
    this.notify();
  }

  private handleProviderError = (event: Event | { message?: string }): void => {
    const message =
      (event as { message?: string }).message ??
      (event instanceof Event ? `WebSocket error (${event.type})` : 'WebSocket error');
    this.setError({ kind: 'ws-error', message, ts: Date.now() });
  };

  private handleProviderClose = (
    event: { code?: number; reason?: string } | Event | undefined,
  ): void => {
    const code = (event as { code?: number } | undefined)?.code ?? undefined;
    const reason = (event as { reason?: string } | undefined)?.reason?.trim() ?? '';
    // 1000 (Normal Closure) and 1001 (Going Away) aren't actionable —
    // the provider will reconnect. Don't surface those as errors.
    if (code === 1000 || code === 1001 || code === undefined) return;
    // The server refused this socket's token (expired, revoked): drop it from
    // the JWT caches, so the provider's reconnect mints a fresh one instead of
    // re-sending the refused one — as the UserDo engine does. Without this an
    // open note re-sent an expired token on every reconnect: ~71k
    // `JWTExpired` events in a week from ONE user (FLASHKA-DURABLE-OBJECT-J).
    if (code === 4401 || code === 4403) {
      notifySocketCredentialRejected(socketToken(this.provider.url));
    }
    const message = reason ? `closed (${code}): ${reason}` : `closed (${code})`;
    this.setError({ kind: 'ws-closed', message, ts: Date.now() });
  };

  /**
   * Push a `yjs-update` entry to the shared devtools log. Same
   * direction-classification as `hooks.tsx` did for the root doc:
   *   - origin === idb       → "idb" (cache replay, no network)
   *   - origin === provider  → "in"  (remote update applied locally)
   *   - origin == null       → "out" (local edit; provider will flush
   *                                   it upstream)
   *   - other object origin  → "out" with the constructor name in
   *                                   detail (rare — e.g. tiptap
   *                                   `setContent` writes that don't
   *                                   propagate an explicit origin)
   */
  private handleDocUpdate = (update: Uint8Array, origin: unknown): void => {
    let direction: 'out' | 'in' | 'idb';
    let detail: string;
    if (origin != null && origin === this.idb) {
      direction = 'idb';
      detail = 'IndexedDB replay';
    } else if (origin != null && origin === this.provider) {
      direction = 'in';
      detail = 'remote → local';
    } else if (origin == null) {
      direction = 'out';
      detail = 'local edit';
    } else {
      direction = 'out';
      const name =
        typeof origin === 'object'
          ? ((origin as { constructor?: { name?: string } }).constructor?.name ??
            'Object')
          : String(origin);
      detail = `local (${name})`;
    }
    pushSyncMessage({
      source: logSourceForParty(this.location.party),
      direction,
      type: 'yjs-update',
      ts: Date.now(),
      sizeBytes: update.byteLength,
      // Include the room (groupId for SG, userId for personal) in the
      // detail. The devtools StudyGroupDO messages tab filters
      // `messageLog.filter((m) => m.detail?.includes(selectedGroupId))`
      // (see sync-devtools.tsx) — without the room here, per-note
      // SG entries get dropped from the list even though their
      // floater fires.
      detail: detail.startsWith('IndexedDB')
        ? `${detail} — room ${this.location.room} note ${this.location.deckId}/${this.location.noteId}`
        : `room ${this.location.room} note ${this.location.deckId}/${this.location.noteId} ${detail}`,
    });
  };

  private notify(): void {
    for (const l of this.listeners) {
      try {
        l();
      } catch {
        // ignore listener errors
      }
    }
  }
}

/** The `token` query param a socket connected with, if any. */
function socketToken(url: string | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).searchParams.get('token');
  } catch {
    return null;
  }
}
